<template>
  <div class="home">
    <!-- Hero Section -->
    <div class="relative">
      <VideoHero
        poster-image="@/assets/images/hero-poster.jpg"
        @video-loaded="$emit('video-loaded', $event)"
      />
    </div>

    <!-- Marquee Text Section -->
    <MarqueeText :speed="600" direction="left" />

    <!-- About Section -->
    <section class="bg-white py-20 md:py-30 lg:py-40 px-6">
      <div class="max-w-6xl mx-auto">
        <div class="text-center max-w-4xl mx-auto">
          <h1 class="text-4xl md:text-5xl lg:text-6xl font-light mb-8 md:mb-16 leading-tight" v-html="heroTitle">
          </h1>
          <div class="space-y-4 text-lg md:text-xl text-black">
            <p v-for="(paragraph, index) in heroDescription" :key="index" class="leading-relaxed" v-html="paragraph">
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Video Falamos Section -->
    <section class="relative w-full overflow-hidden">
      <div class="relative w-full aspect-video sm:aspect-[16/9] md:aspect-[21/9] lg:aspect-[16/9] xl:aspect-[21/9] video-falamos-container">
        <div class="video-bg">
          <iframe 
            ref="homeVideo"
            src="https://player.vimeo.com/video/1104929999?background=1&autoplay=1&loop=1&byline=0&title=0&muted=1&controls=0&portrait=0&sidedock=0&dnt=1&transparent=0"
            frameborder="0"
            allow="autoplay; fullscreen; picture-in-picture"
          ></iframe>
        </div>
        <div class="absolute inset-0 bg-black bg-opacity-10 z-10"></div>
      </div>
    </section>

    <!-- Marquee Text Dark Section -->
    <MarqueeTextDark :speed="1200" />

    <!-- Services Section -->
    <section class="bg-black text-white py-20 px-6 overflow-x-hidden toggle-white">
      <div class="max-w-6xl mx-auto">
        <h2 class="text-2xl md:text-3xl font-light mb-16 border-b border-white pb-6">
          {{ t('home.services.title') }}
        </h2>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 px-0 items-start">
          <!-- Left Column -->
          <div class="flex flex-col space-y-6 md:space-y-8">
            <div 
              v-for="serviceId in leftServices" 
              :key="serviceId"
              class="left-column-item toggle-item"
            >
              <div class="toggle-modo-item" @click="toggleItem(serviceId)">
                <div class="flex flex-col">
                  <span class="text-lg md:text-xl font-light leading-relaxed">{{ t(`home.services.items.${serviceId}.title`) }}</span>
                  <span v-if="hasSubtitle(serviceId)" class="text-sm text-white mt-2 font-light">{{ t(`home.services.items.${serviceId}.subtitle`) }}</span>
                </div>
                <svg 
                  class="w-5 h-5 flex-shrink-0 ml-4 transform transition-transform duration-300"
                  :class="{ 'rotate-180': openItems[serviceId] }"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
                </svg>
              </div>
              <div 
                class="overflow-hidden transition-all duration-500 ease-in-out"
                :class="openItems[serviceId] ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'"
              >
                <div class="pt-4 pb-2 text-gray-300 text-lg leading-relaxed">
                  <p>{{ t(`home.services.items.${serviceId}.description`) }}</p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Right Column -->
          <div class="flex flex-col space-y-6 md:space-y-8">
            <div 
              v-for="serviceId in rightServices" 
              :key="serviceId"
              class="right-column-item toggle-item"
            >
              <div class="toggle-modo-item" @click="toggleItem(serviceId)">
                <div class="flex flex-col">
                  <span class="text-lg md:text-xl font-light leading-relaxed">{{ t(`home.services.items.${serviceId}.title`) }}</span>
                  <span v-if="hasSubtitle(serviceId)" class="text-sm text-white mt-2 font-light">{{ t(`home.services.items.${serviceId}.subtitle`) }}</span>
                </div>
                <svg 
                  class="w-5 h-5 flex-shrink-0 ml-4 transform transition-transform duration-300"
                  :class="{ 'rotate-180': openItems[serviceId] }"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
                </svg>
              </div>
              <div 
                class="overflow-hidden transition-all duration-500 ease-in-out"
                :class="openItems[serviceId] ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'"
              >
                <div class="pt-4 pb-2 text-gray-300 text-lg leading-relaxed">
                  <p>{{ t(`home.services.items.${serviceId}.description`) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Modo Operandi Section -->
    <section class="bg-white text-black py-20 px-6 overflow-x-hidden">
      <div class="max-w-6xl mx-auto">
        <h2 class="text-xl md:text-3xl font-light mb-16 border-b border-black pb-6 text-right">
          <span class="font-light font-cormorant italic">{{ t('home.modoOperandi.title') }}</span> <span class="font-medium">{{ t('home.modoOperandi.subtitle') }}</span>
        </h2>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 px-0 items-start">
          <!-- Left Column -->
          <div class="flex flex-col space-y-6 md:space-y-8">
            <div 
              v-for="modoId in leftModos" 
              :key="modoId"
              class="modo-item modo-item-left toggle-item"
            >
              <div class="toggle-modo-item" @click="toggleModoItem(modoId)">
                <span class="text-lg md:text-xl font-light leading-relaxed">{{ t(`home.modoOperandi.items.${modoId}.title`) }}</span>
                <svg 
                  class="w-5 h-5 flex-shrink-0 ml-4 transform transition-transform duration-300"
                  :class="{ 'rotate-180': modoOpenItems[modoId] }"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
                </svg>
              </div>
              <div 
                class="overflow-hidden transition-all duration-500 ease-in-out"
                :class="modoOpenItems[modoId] ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'"
              >
                <div class="pt-4 pb-2 text-black text-lg leading-relaxed">
                  <p>{{ t(`home.modoOperandi.items.${modoId}.description`) }}</p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Right Column -->
          <div class="flex flex-col space-y-6 md:space-y-8">
            <div 
              v-for="modoId in rightModos" 
              :key="modoId"
              class="modo-item modo-item-right toggle-item"
            >
              <div class="toggle-modo-item" @click="toggleModoItem(modoId)">
                <span class="text-lg md:text-xl font-light leading-relaxed">{{ t(`home.modoOperandi.items.${modoId}.title`) }}</span>
                <svg 
                  class="w-5 h-5 flex-shrink-0 ml-4 transform transition-transform duration-300"
                  :class="{ 'rotate-180': modoOpenItems[modoId] }"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
                </svg>
              </div>
              <div 
                class="overflow-hidden transition-all duration-500 ease-in-out"
                :class="modoOpenItems[modoId] ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'"
              >
                <div class="pt-4 pb-2 text-black text-lg leading-relaxed">
                  <p>{{ t(`home.modoOperandi.items.${modoId}.description`) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { onMounted, reactive, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import VideoHero from '@/components/VideoHero.vue'
import MarqueeText from '@/components/MarqueeText.vue'
import MarqueeTextDark from '@/components/MarqueeTextDark.vue'
import { useScrollAnimations } from '@/composables/useScrollAnimations'
import pt from '@/i18n/locales/pt.js'
import enUS from '@/i18n/locales/en-US.js'

// Constants
const ANIMATION_CONFIG = {
  mobile: {
    duration: 0.8,
    delay: 0.2,
    reverse: true
  },
  desktop: {
    duration: 0.8,
    delay: 0.2,
    reverse: true
  }
}

const BREAKPOINTS = {
  mobile: 768
}

// Data configurations - will be replaced with translations
const SERVICES_DATA = {
  leftServices: ['branding', 'identidade', 'campanhas'],
  rightServices: ['conteudo', 'digital', 'consultoria']
}

const MODOS_DATA = {
  leftModos: ['agilidade', 'parceria', 'cultura'],
  rightModos: ['envolvimento', 'proximidade', 'foco']
}

export default {
  name: 'Home',
  components: {
    VideoHero,
    MarqueeText,
    MarqueeTextDark
  },
  emits: ['video-loaded'],

  setup() {
    const { fadeInLeft, fadeInRight } = useScrollAnimations()
    const { t, locale } = useI18n()
    
    const heroTitle = computed(() => {
      return t('home.hero.title').replace('CRAFT', '<span class="italic font-cormorant">Craft</span>')
    })
    
    const heroDescription = computed(() => {
      // Acessar diretamente o objeto de traduções
      const messages = locale.value === 'pt' ? pt : enUS
      return messages.home.hero.description
    })
    

    
    // State for controlling open/closed items
    const openItems = reactive({
      branding: false,
      identidade: false,
      campanhas: false,
      conteudo: false,
      digital: false,
      consultoria: false
    })

    // State for controlling Modo Operandi section items
    const modoOpenItems = reactive({
      agilidade: false,
      parceria: false,
      cultura: false,
      envolvimento: false,
      proximidade: false,
      foco: false
    })
    
    /**
     * Toggle service item visibility
     * @param {string} item - Item identifier
     */
    const toggleItem = (item) => {
      openItems[item] = !openItems[item]
    }

    /**
     * Toggle modo operandi item visibility
     * @param {string} item - Item identifier
     */
    const toggleModoItem = (item) => {
      modoOpenItems[item] = !modoOpenItems[item]
    }
    
    /**
     * Initialize scroll animations based on device type
     */
    const initializeAnimations = () => {
      const isMobile = window.innerWidth <= BREAKPOINTS.mobile

      if (isMobile) {
        // Single animation for all elements on mobile
        fadeInLeft('.left-column-item, .right-column-item, .modo-item', ANIMATION_CONFIG.mobile)
      } else {
        // Separate animations for desktop
        // Services section
        fadeInLeft('.left-column-item', ANIMATION_CONFIG.desktop)
        fadeInRight('.right-column-item', ANIMATION_CONFIG.desktop)
        
        // Modo Operandi section
        fadeInLeft('.modo-item-left', ANIMATION_CONFIG.desktop)
        fadeInRight('.modo-item-right', ANIMATION_CONFIG.desktop)
      }
    }

    /**
     * Setup video loading detection
     */
    const setupVideoLoading = () => {
      const iframe = document.querySelector('#app iframe[src*="1104929999"]')
      if (iframe) {
        iframe.addEventListener('load', () => {
          // Emitir evento para o App.vue
          window.dispatchEvent(new CustomEvent('video-loaded', { detail: 'home' }))
        })
        
        // Fallback: se o iframe já estiver carregado
        if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
          window.dispatchEvent(new CustomEvent('video-loaded', { detail: 'home' }))
        }
      }
    }
    
    onMounted(() => {
      initializeAnimations()
      setupVideoLoading()
    })
    
    /**
     * Check if a service has a subtitle
     * @param {string} serviceId - Service identifier
     * @returns {boolean} - True if subtitle exists and is not empty
     */
    const hasSubtitle = (serviceId) => {
      try {
        const subtitle = t(`home.services.items.${serviceId}.subtitle`)
        // Verificar se a tradução existe e não é a própria chave
        return subtitle && 
               subtitle.trim() !== '' && 
               !subtitle.includes('home.services.items.') &&
               subtitle !== `home.services.items.${serviceId}.subtitle`
      } catch (error) {
        return false
      }
    }
    
    return {
      t,
      heroTitle,
      heroDescription,
      openItems,
      modoOpenItems,
      toggleItem,
      toggleModoItem,
      hasSubtitle,
      leftServices: SERVICES_DATA.leftServices,
      rightServices: SERVICES_DATA.rightServices,
      leftModos: MODOS_DATA.leftModos,
      rightModos: MODOS_DATA.rightModos
    }
  }
}
</script>

<style scoped>
/* Video Falamos Section - Responsive based on video ratio */
.video-falamos-container {
  aspect-ratio: 16/9;
}

@media (min-width: 640px) {
  .video-falamos-container {
    aspect-ratio: 16/9;
  }
}

@media (min-width: 768px) {
  .video-falamos-container {
    aspect-ratio: 21/9;
  }
}

@media (min-width: 1024px) {
  .video-falamos-container {
    aspect-ratio: 16/9;
  }
}

@media (min-width: 1280px) {
  .video-falamos-container {
    aspect-ratio: 21/9;
  }
}

/* Video styles */
.video-falamos-container video {
  object-fit: cover;
  object-position: center center;
}

/* Video background styles */
.video-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.video-bg iframe {
  width: 100vw;
  height: 56.25vw; /* 16:9 aspect ratio */
  min-height: 100%;
  min-width: 177.77vh; /* 16:9 aspect ratio */
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* Toggle item styles */
.toggle-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.toggle-item > div:first-child {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  min-height: 80px;
}

/* Grid layout */
.grid {
  align-items: start;
}

.flex.flex-col {
  align-self: flex-start;
}

/* Toggle button base styles */
.toggle-modo-item {
  @apply flex items-center justify-between min-h-[80px] pb-6 cursor-pointer transition-colors;
}

/* White background section variant (Modo Operandi) */
.toggle-modo-item {
  @apply border-b border-black px-0 mx-0;
}

/* Black background section variant (Services) */
.toggle-white .toggle-modo-item {
  @apply border-b border-white px-0 mx-0;
}
</style> 